import React, {Component, ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../../shared/hoc/createAdminPage'
import {Nullable} from '../../../app/TypeHelpers'
import {AdminReserveLocationState, ReservationInfo, ReservationOptionInfo} from './AdminReserveInterfaces'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {AdminAgentReserveSteps, AdminManagerReserveSteps, AdminReserveStepper} from './AdminReserveStepper'
import {LeftRowFlexContainer, RowFlexContainer} from '../../../styledComponents/FlexComponents'
import {Box, Paper, Table, TableBody, TableCell, TableHead, TableRow} from '@mui/material';
import {PageFooterArea, PageFotterAreaLeft, PageFotterAreaRight} from '../../shared/hoc/withAdminPageTemplate'
import {BackLink} from '../../../styledComponents/adminPage/Links'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import {addEmptyData, isNoSelect, NoSelectedValue} from '../../../componentHelper/selectHelper'
import {CHARGE_UNIT, PAYMENT_TYPE, VALID_OR_NOT} from '../../../CodeMaster'
import {InnerMiniTableBody, RequiredChip} from '../../../styledComponents/adminPage/Table'
import {formatNumberWithCommas} from '../../../formatters/formatNumberWithCommas'
import PPSelect from '../../shared/forms/PPSelect'
import {ReserveOptionPaymentCalculator} from '../paymentCalculators/ReserveOptionPaymentCalculator'
import FacilityForReservation from '../../facilities/FacilityForReservation'
import lsHelper from './AdminReserveLocationStateHelper'
import {createPaymentViewData, getDisplayRoomNumber, getDisplaySelectedUserOrRoleName} from './AdminReserveHelper'
import {BoldSpan} from '../../../styledComponents/StyledSpan'
import {NoMarginInnerTable, NoPLMinimumWidthTableCell} from './AdminReserveStyledComponent'
import {FacilityRentalType, ReservationClass} from '../ReservationInterfaces'
import styled from 'styled-components'
import {SelectChangeEvent} from '@mui/material/Select'
import {Location} from 'react-router-dom'

interface FacilityOption {
    optionId: number
    optionName: string
    chargeWithUnit: string
    minimumQuantity: number
    maximumQuantity: number
    unitPerChargeUnit: string
    required: boolean
    paymentType: PAYMENT_TYPE
    sortOrder: number
    selectedQuantity: Nullable<number>
    selectedPaymentType: PAYMENT_TYPE
}

interface Props {
    classification: ReservationClass
    condominiumCd: string
    location: Location<AdminReserveLocationState>
}

interface State {
    facilityOptionList: FacilityOption[]
}

const KEY_PREVIOUS_PAGE_URL: string = 'AdminReserveSelectOptionPage.previousPageUrl'
const KEY_PREVIOUS_PAGE_NAME: string = 'AdminReserveSelectOptionPage.previousPageName'

class AdminReserveSelectOptionPage extends Component<Props & AdminPagePropsSet, State> {
    private locationState: AdminReserveLocationState
    private previousPageUrl: string
    private previousPageName: string

    private readonly optionListHeadLabelsForAgent: string[] =
        ['オプション', '料金', '数量', 'お支払方法']
    private readonly optionListHeadLabelsForManager: string[] =
        ['オプション', '料金', '数量']
    private reserveStartDate: string
    private reserveStartTime: Nullable<string>
    private reserveEndDate: Nullable<string>
    private reserveEndTime: Nullable<string>

    private facilityData: FacilityForReservation

    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        if (!this.props.location || !this.props.location.state || !this.props.location.state.reservationInfo) {
            throw Error('前画面からの引き継ぎ情報がありません。画面遷移が不正です。')
        }
        const {previousPageUrl, previousPageName, ...rest} = this.props.location.state
        this.locationState = lsHelper.fromObject(rest)

        if (this.props.classification !== this.locationState.reservationInfo.classification) {
            throw Error('渡された予約情報が画面と合っていません。画面遷移が不正です。')
        }

        if (this.locationState.facilityData === null) {
            throw Error('施設情報がありません。画面遷移が不正です。')
        }
        this.facilityData = this.locationState.facilityData

        if (previousPageUrl !== undefined && previousPageName !== undefined) {
            this.previousPageUrl = previousPageUrl
            this.previousPageName = previousPageName
            SessionStorageHelper.setItem(KEY_PREVIOUS_PAGE_URL, previousPageUrl)
            SessionStorageHelper.setItem(KEY_PREVIOUS_PAGE_NAME, previousPageName)
        } else {
            const url: Nullable<string> = SessionStorageHelper.getStringItemOrNull(KEY_PREVIOUS_PAGE_URL)
            const name: Nullable<string> = SessionStorageHelper.getStringItemOrNull(KEY_PREVIOUS_PAGE_NAME)
            if (!url || !name) {
                throw Error('前画面のリンク情報がありません。画面遷移が不正です。')
            }
            this.previousPageUrl = url || ''
            this.previousPageName = name || ''
        }

        const {reservationInfo} = this.locationState
        this.reserveStartDate = reservationInfo.startDate
        if (reservationInfo.type === FacilityRentalType.DURATION) {
            this.reserveStartTime = reservationInfo.startTime
            this.reserveEndDate = reservationInfo.endDate
            this.reserveEndTime = reservationInfo.endTime
        } else {
            this.reserveStartTime = null
            this.reserveEndDate = null
            this.reserveEndTime = null
        }

        let facilityOptionList = this.createFacilityOptions()

        if (reservationInfo.reservationOptionInfos !== null) {
            facilityOptionList =
                this.restoreFromLocationState(facilityOptionList, reservationInfo.reservationOptionInfos)
        }

        this.state = {facilityOptionList}

        this.handleChangeQuantity = this.handleChangeQuantity.bind(this)
        this.handleChangePaymentType = this.handleChangePaymentType.bind(this)
        this.handleClickPreviousPage = this.handleClickPreviousPage.bind(this)
        this.handleClickSubmit = this.handleClickSubmit.bind(this)
    }

    createFacilityOptions(): FacilityOption[] {
        return this.facilityData.facilityOptions.map(option => {
            const outlineCharge = formatNumberWithCommas(option.charge)
            const outlineUnitTime = (option.chargeUnit !== CHARGE_UNIT.FIXED)
                ? `${option.chargeUnitTime}${option.chargeUnit.label}`
                : option.chargeUnit.label
            const required = option.appointRequired === VALID_OR_NOT.VALID
            return {
                optionId: option.optionId,
                optionName: option.name,
                chargeWithUnit: `${outlineCharge} 円`,
                minimumQuantity: option.volumeMin,
                maximumQuantity: option.volumeMax,
                unitPerChargeUnit: `${option.unitName} / ${outlineUnitTime}`,
                required: required,
                paymentType: option.paymentMethod,
                sortOrder: option.sortOrder,
                selectedQuantity: (required) ? option.volumeMin : null,
                selectedPaymentType: option.paymentMethod,
            }
        }).sort((a, b) => a.sortOrder - b.sortOrder)
    }

    restoreFromLocationState(
        facilityOptions: FacilityOption[], reservationOptions: ReservationOptionInfo[],
    ): FacilityOption[] {
        return [...facilityOptions].map(option => {
            const reserve = reservationOptions.find(reserveOpt => reserveOpt.optionId === option.optionId)
            return reserve === undefined ? option : {
                ...option,
                selectedQuantity: reserve.quantity,
                selectedPaymentType: reserve.paymentType,
            }
        })
    }

    handleChangeQuantity(targetOption: FacilityOption): (event: SelectChangeEvent<unknown>) => void {
        return (event: SelectChangeEvent<unknown>) => {
            const changedOptionValue = isNoSelect(event.target.value) ? null : Number(event.target.value)

            this.setState(prevState => {
                const {facilityOptionList} = prevState
                const changedOptionIndex =
                    facilityOptionList.findIndex(option => (option === targetOption))

                const newFacilityOption: FacilityOption = {
                    ...targetOption,
                    selectedQuantity: changedOptionValue,
                }

                const replaced: { facilityOptionList: FacilityOption[] } = {
                    facilityOptionList: [
                        ...facilityOptionList.slice(0, changedOptionIndex),
                        newFacilityOption,
                        ...facilityOptionList.slice(changedOptionIndex + 1),
                    ],
                }
                return replaced
            })
        }
    }

    handleChangePaymentType(targetOption: FacilityOption): (event: SelectChangeEvent<unknown>) => void {
        return (event: SelectChangeEvent<unknown>) => {
            const changedPaymentType = PAYMENT_TYPE.codeToEnumConstants(event.target.value as string)

            this.setState(prevState => {
                const {facilityOptionList} = prevState
                const changedOptionIndex =
                    facilityOptionList.findIndex(option => (option === targetOption))

                const newFacilityOption: FacilityOption = {
                    ...targetOption,
                    selectedPaymentType: changedPaymentType,
                }

                const replaced: { facilityOptionList: FacilityOption[] } = {
                    facilityOptionList: [
                        ...facilityOptionList.slice(0, changedOptionIndex),
                        newFacilityOption,
                        ...facilityOptionList.slice(changedOptionIndex + 1),
                    ],
                }
                return replaced
            })
        }
    }

    handleClickPreviousPage(): void {
        this.props.navigate(
            this.previousPageUrl,
            {
                replace: true,
                state: lsHelper.toObject(this.locationState),
            },
        )
    }

    handleClickSubmit(): void {
        const reservationOptionInfos: ReservationOptionInfo[] =
            getSelectedReservationOptionInfos(this.state.facilityOptionList)
        const {reservationInfo, ...rest} = this.locationState
        const newReservationInfo: ReservationInfo = {...reservationInfo, reservationOptionInfos}
        const classificationString = this.props.classification
        const newAdminReserveLocationState: AdminReserveLocationState = {
            ...rest,
            reservationInfo: newReservationInfo,
            previousPageUrl:
                `/admin/condominiums/${this.props.condominiumCd}/${classificationString}-reserve/select-option`,
            previousPageName: 'オプション選択',
        }

        this.props.navigate(
            `/admin/condominiums/${this.props.condominiumCd}/${classificationString}-reserve/confirm`,
            {
                replace: true,
                state: lsHelper.toObject(newAdminReserveLocationState),
            },
        )
    }

    renderActionArea(): ReactNode {
        const headers = (this.props.classification === ReservationClass.AGENT) ?
            this.optionListHeadLabelsForAgent : this.optionListHeadLabelsForManager

        return (<>
            <Table>
                <TableHead>
                    <TableRow>
                        {headers.map((label, index) => <TableCell key={index}>{label}</TableCell>)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {this.state.facilityOptionList.map((option, index) => this.renderOptionListRow(option, index))}
                </TableBody>
            </Table>
            <RowFlexContainer>
                <Box/>
                <Box mt={'8px'} mb={'8px'}>{this.renderPaymentView()}</Box>
            </RowFlexContainer>
        </>)
    }

    renderOptionListRow(option: FacilityOption, key: number): ReactNode {
        const {
            optionName,
            chargeWithUnit,
            minimumQuantity,
            maximumQuantity,
            unitPerChargeUnit,
            required,
            selectedQuantity,
            selectedPaymentType,
        } = option

        const selectableItems =
            new Array<number>((maximumQuantity - minimumQuantity + 1))
                .fill(0)
                .map((_, index) => (`${minimumQuantity + index}`))
        const selectableOptionItems = selectableItems.map(item => ({value: item, label: item}))
        const optionItems = (required) ? selectableOptionItems : addEmptyData(selectableOptionItems)

        return (<TableRow key={key}>
            <OptionNameCell>
                <LeftRowFlexContainer>
                    {optionName}
                    {
                        required &&
                        (<Box ml={'20px'}><RequiredChip label={'必須'}/></Box>)
                    }
                </LeftRowFlexContainer>
            </OptionNameCell>
            <ChargeWithUnitCell>
                {chargeWithUnit}
            </ChargeWithUnitCell>
            <QuantityCell>
                <RowFlexContainer>
                    <PPSelect
                        data={optionItems}
                        onChange={this.handleChangeQuantity(option)}
                        value={selectedQuantity || NoSelectedValue}
                    />
                    <Box flexGrow={10}>
                        {unitPerChargeUnit}
                    </Box>
                </RowFlexContainer>
            </QuantityCell>
            {
                (this.locationState.reservationInfo.classification === ReservationClass.AGENT) &&
                <PaymentTypeCell>
                    <PPSelect
                        data={PAYMENT_TYPE.values()}
                        onChange={this.handleChangePaymentType(option)}
                        value={selectedPaymentType.value}
                    />
                </PaymentTypeCell>
            }
        </TableRow>)
    }

    renderPaymentView(): ReactNode {
        const {facilityOptions} = this.facilityData
        const calculator: ReserveOptionPaymentCalculator = new ReserveOptionPaymentCalculator(
            facilityOptions,
            getSelectedReservationOptionInfos(this.state.facilityOptionList),
            this.reserveStartDate,
            this.reserveStartTime,
            this.reserveEndDate,
            this.reserveEndTime,
        )

        const paymentItems = calculator.calculate()
        const totalCharge = paymentItems
            .map(item => item.charge || 0)
            .reduce((accumulator, charge) => accumulator + charge, 0)

        const paymentViewInfos = createPaymentViewData(
            paymentItems,
            this.locationState.reservationInfo,
        )

        return (<>
            {
                <NoMarginInnerTable>
                    <InnerMiniTableBody>
                        {
                            paymentViewInfos.map((info, index) => {
                                return (<TableRow key={index}>
                                    <NoPLMinimumWidthTableCell>
                                        <BoldSpan>
                                            {`${info.article}：${info.quantityWithUnit}`}
                                        </BoldSpan>
                                    </NoPLMinimumWidthTableCell>
                                    <NoPLMinimumWidthTableCell align={'right'}>
                                        <BoldSpan>
                                            {info.charge}
                                        </BoldSpan>
                                    </NoPLMinimumWidthTableCell>
                                </TableRow>)
                            })
                        }
                    </InnerMiniTableBody>
                </NoMarginInnerTable>
            }
            <Box mt={'10px'} mb={'10px'}>
                <BoldSpan>
                    <RowFlexContainer>
                        <Box pr={'16px'}>合計</Box>
                        <Box pr={'16px'}>
                            {
                                (this.props.classification === ReservationClass.AGENT)
                                    ? formatNumberWithCommas(totalCharge) : 0
                            }円
                        </Box>
                    </RowFlexContainer>
                </BoldSpan>
            </Box>
        </>)
    }

    createDisplayUserMessage(): ReactNode {
        let userMessageNode: ReactNode
        if (this.locationState.reservationInfo.classification === ReservationClass.AGENT) {
            const {userInfo} = this.locationState.reservationInfo
            if (userInfo === null) {
                throw Error('ユーザ情報がありません。')
            }

            const displayRoomNumber = getDisplayRoomNumber(userInfo)
            const displaySelectedUserName = getDisplaySelectedUserOrRoleName(userInfo)

            userMessageNode = <>
                <BoldSpan>{this.facilityData.facilityName}</BoldSpan> を <BoldSpan>{`${displayRoomNumber} ${displaySelectedUserName}`}</BoldSpan> さんで代行予約受付
            </>
        } else {
            const {userInfo} = this.locationState.reservationInfo
            if (userInfo === null) {
                throw Error('ユーザ情報がありません。')
            }
            userMessageNode = <>
                <BoldSpan>{this.facilityData.facilityName}</BoldSpan> を <BoldSpan>{`${userInfo.familyName} ${userInfo.firstName}`}</BoldSpan> さんで管理者予約受付
            </>
        }

        return (<>
            {userMessageNode}
            <div>オプションが必要な場合は選択してください。</div>
        </>)
    }

    render(): ReactNode {
        return (<>
            {
                this.props.classification === ReservationClass.AGENT
                    ? <Box height={'108px'} bgcolor={'#ffffff'} padding={'24px'}>
                        <AdminReserveStepper
                        classification={ReservationClass.AGENT}
                        step={AdminAgentReserveSteps.SELECT_OPTION}
                        hasOption={true}
                    />
                    </Box>
                    : <Box height={'108px'} bgcolor={'#ffffff'} padding={'24px'}>
                        <AdminReserveStepper
                        classification={ReservationClass.MANAGER}
                        step={AdminManagerReserveSteps.SELECT_OPTION}
                        hasOption={true}
                    />
                    </Box>
            }
            <Box mt={'10px'} mb={'10px'}>
                {this.createDisplayUserMessage()}
            </Box>

            <Paper>
                {this.renderActionArea()}
            </Paper>
            <PageFooterArea>
                <PageFotterAreaLeft>
                    <BackLink onClick={this.handleClickPreviousPage}>
                        {`${this.previousPageName}に戻る`}
                    </BackLink>
                </PageFotterAreaLeft>
                <PageFotterAreaRight>
                    <AdminPrimaryButton
                        onClick={this.handleClickSubmit}
                    >
                        予約内容確認に進む
                    </AdminPrimaryButton>
                </PageFotterAreaRight>
            </PageFooterArea>

        </>)
    }
}

function getSelectedReservationOptionInfos(options: FacilityOption[]): ReservationOptionInfo[] {
    return options.filter(option => option.selectedQuantity !== null)
        .map(option => {
            return {
                optionId: option.optionId,
                quantity: option.selectedQuantity || 0,
                sortOrder: option.sortOrder,
                paymentType: option.selectedPaymentType,
            }
        })
}

const OptionNameCell = styled(TableCell)`
  width: 35%
`

const ChargeWithUnitCell = styled(TableCell)`
  width: 20%
`

const QuantityCell = styled(TableCell)`
  width: 25%
`

const PaymentTypeCell = styled(TableCell)`
  width: 20%
`

export default createAdminPage(AdminReserveSelectOptionPage)
